import { useEffect, useLayoutEffect, useState } from 'react';
import { C } from '@ridgewill/brand';
import { RAIL_GAP, RAIL_WIDTH, STICKY_TOP } from '../lib/layout.js';
import { ChangePassword } from '../sections/ChangePassword.js';
import { EmailAddress } from '../sections/EmailAddress.js';
import { RecoveryAddress } from '../sections/RecoveryAddress.js';
import { SignInMethods } from '../sections/SignInMethods.js';
import { TwoFactor } from '../sections/TwoFactor.js';
import { Passkeys } from '../sections/Passkeys.js';
import { Sessions } from '../sections/Sessions.js';
import { CloseAccount } from '../sections/CloseAccount.js';
import {
  loadAvailableProviders,
  loadSignInMethods,
  type SignInMethods as Methods,
} from '../lib/sign-in-methods.js';
import {
  loadPasskeyCount,
  loadPendingChange,
  type PendingChangeState,
} from '../lib/email-change.js';
import { loadRecoveryAddress, type RecoveryState } from '../lib/recovery-address.js';

/**
 * Everything about how you get in, and what is currently signed in.
 *
 * ── ONE SCREEN, FOUR SECTIONS, and it used to be three tabs ─────────
 *
 * These are not four subjects. They are one — *how do I get into this account,
 * and who currently can?* Separate tabs described the implementation (a
 * credential type per plugin) rather than the question the person arrived with,
 * and the split hid the fact that changing a password was not possible at all.
 *
 * ── A DIVIDER WAS NOT ENOUGH SEPARATION ─────────────────────────────
 *
 * The first version stacked the four with a horizontal rule between them, and
 * it read as one long page rather than four things. Each section is now a
 * bordered card with its own heading, which is what actually makes a boundary
 * visible — a rule is a hint, a container is a statement.
 *
 * ── THE CONTENTS RAIL IS NAVIGATION, NOT DECORATION ─────────────────
 *
 * Four sections is enough that "where is the passkey bit" costs a scroll. The
 * rail is sticky so the answer stays on screen, and it is a `nav` with real
 * anchors, so it works before hydration, survives a middle-click, and gives a
 * screen-reader user the same overview a sighted one gets from the left margin.
 *
 * It is hidden below 1024px rather than reflowed. A contents list stacked above
 * the content it describes is just a second copy of the headings, and on a
 * narrow screen scrolling past four cards is not the problem it solves.
 *
 * ── IT NEEDED TO SAY WHERE YOU ARE ──────────────────────────────────
 *
 * A list of four links with nothing marked is a menu, not an index. The first
 * version was exactly that and read as decoration.
 *
 * `IntersectionObserver` with a top-biased root margin marks the section
 * currently under the top of the viewport. A scroll listener recomputing
 * offsets would do the same thing and run on every frame; the observer fires
 * only on a boundary crossing.
 *
 * The marker is a left border plus weight, not colour alone. Colour alone fails
 * for anyone who cannot distinguish it, and this is the only state the rail has.
 *
 * ── AND THE CARDS HAD TO FILL THE WIDTH ─────────────────────────────
 *
 * The sections carried `maxWidth: 640` from when each was its own page. Inside a
 * flex row next to a 12rem rail that left a band of empty white between the two,
 * which read as a layout fault rather than a measure. The cards now fill the
 * column and the READING measure is held on the prose inside them, which is
 * where it belongs.
 */
const SECTIONS = [
  /**
   * FIRST, above everything, because the address IS a credential here
   * (account#54).
   *
   * Every provider sign-in resolves through the email address
   * (RidgeWill/auth#324), so whoever holds the mailbox holds the account. It
   * also carries the pending-change card, which is the one thing on this page
   * somebody may need to act on before they read anything else: a change they
   * did not ask for is visible and cancellable here, and nowhere else.
   */
  { id: 'email', label: 'Email' },
  /**
   * SECOND, and above Password on purpose (Jon, 2026-08-29).
   *
   * It is the section that makes the one below it legible: "you do not have a
   * password" is alarming read cold and unremarkable directly under a line
   * saying you sign in with Google. Ordering is the cheapest way to say that,
   * and it costs nothing for the accounts where the answer is a password —
   * those read "email and password" and carry on down the page.
   */
  /**
   * DIRECTLY UNDER THE ADDRESS IT BACKS UP (account#73).
   *
   * The two are one subject read in order: here is the address your account
   * lives at, and here is what happens if you lose it. Anywhere further down
   * and it reads as an unrelated setting, which is how a recovery address
   * ends up unset by the people who most need one.
   *
   * "Recovery" rather than "Recovery address" in the rail, because the labels
   * here are one or two words and a long one would wrap against short
   * neighbours.
   */
  { id: 'recovery', label: 'Recovery' },
  { id: 'sign-in-methods', label: 'SSO Providers' },
  { id: 'password', label: 'Password' },
  { id: 'two-factor', label: 'Two-factor' },
  { id: 'passkeys', label: 'Passkeys' },
  { id: 'devices', label: 'Devices' },
  /**
   * Last, and named for what it is. auth#165.
   *
   * Bottom of the screen because ordering is a recommendation: the sections
   * above are things somebody came here to do, and this is the one nobody
   * should arrive at by scrolling past something they wanted. "Danger" rather
   * than "Close account" in the rail so the link itself carries the warning —
   * a neutral label in a list of neutral labels is a thing you click to see
   * what it is.
   */
  { id: 'danger', label: 'Danger' },
] as const;

export function Security({
  twoFactorEnabled,
  onChanged,
  currentToken,
  email,
}: {
  twoFactorEnabled: boolean;
  onChanged: () => void;
  /** Passed down to name the CREDENTIAL, not the passkey. See sections/Passkeys. */
  email: string;
  currentToken?: string;
}) {
  const active = useActiveSection();
  useHashTarget();
  const trailing = useTrailingSpace(SECTIONS[SECTIONS.length - 1]!.id);
  const { methods, available, reload: reloadMethods } = useSignInMethods();
  const { pending, passkeyCount, reload: reloadPending } = usePendingEmailChange();
  const { recovery, reload: reloadRecovery } = useRecoveryAddress();

  return (
    /**
     * `relative` is what the rail positions against, and it is the only reason
     * this wrapper exists. The cards are no longer in a flex row: with the rail
     * out of the flow they simply fill the frame.
     */
    <div style={{ position: 'relative' }}>
      <Contents active={active} />

      <div style={{ minWidth: 0 }}>
        <Card id="email" title="Email address">
          <EmailAddress
            email={email}
            methods={methods}
            twoFactorEnabled={twoFactorEnabled}
            passkeyCount={passkeyCount}
            pending={pending}
            onChanged={reloadPending}
            onSessionChanged={onChanged}
          />
        </Card>
        <Card id="recovery" title="Recovery address">
          <RecoveryAddress
            email={email}
            methods={methods}
            state={recovery}
            onChanged={reloadRecovery}
          />
        </Card>
        <Card id="sign-in-methods" title="Single Sign-On Providers">
          <SignInMethods methods={methods} available={available} onChanged={reloadMethods} />
        </Card>
        <Card id="password" title="Password">
          <ChangePassword methods={methods} />
        </Card>
        <Card id="two-factor" title="Two-factor authentication">
          <TwoFactor
            twoFactorEnabled={twoFactorEnabled}
            hasPassword={methods === undefined ? undefined : methods?.hasPassword !== false}
            onChanged={onChanged}
          />
        </Card>
        <Card id="passkeys" title="Passkeys">
          <Passkeys email={email} />
        </Card>
        <Card id="devices" title="Devices">
          <Sessions currentToken={currentToken} />
        </Card>
        <Card id="danger" title="Danger" tone="danger">
          <CloseAccount />
        </Card>

        {/*
          Scroll room, so the rail can mark every section. See `trailingSpace`
          for the arithmetic and for the bug it fixes.

          `aria-hidden` and no content: it is a scrolling affordance, not a
          region, and a screen-reader user navigating by landmark should not
          meet an empty one after the last card.
        */}
        <div aria-hidden style={{ height: trailing }} />
      </div>
    </div>
  );
}

/**
 * Is a change to the email address outstanding, and would one be gated?
 *
 * ── TWO ANSWERS FROM ONE HOOK, BECAUSE ONE EVENT INVALIDATES BOTH ────
 *
 * `pending` is the card. `passkeyCount` decides only whether the form warns
 * that the current address will have to approve — see
 * `willNeedCurrentAddressApproval`, which is advisory and errs toward silence.
 *
 * They are loaded together rather than separately because a passkey added on
 * this page changes whether the NEXT request is gated, and the section reading
 * a count from before that is the sort of staleness nobody notices until the
 * warning is wrong. One reload point, like the sign-in methods above.
 *
 * ── `undefined` UNTIL ANSWERED, AND `unknown` WHEN IT FAILED ─────────
 *
 * Three states rather than two, for the same reason `methods` has three:
 * rendering "nothing is pending" for a failed request is a claim about
 * somebody's account made from no evidence, and it is the reassuring direction
 * to be wrong in.
 *
 * The passkey count starts at -1 rather than 0. Zero is a real answer meaning
 * "no passkeys, so the gate applies", and starting there would flash a warning
 * about an extra step at accounts that do not need one.
 */
function usePendingEmailChange(): {
  pending: PendingChangeState | undefined;
  passkeyCount: number;
  reload: () => void;
} {
  const [pending, setPending] = useState<PendingChangeState | undefined>(undefined);
  const [passkeyCount, setPasskeyCount] = useState(-1);
  /** Bumped to re-run the effect, so there is one fetch site and the `live`
   *  guard below keeps working. See `useSignInMethods` for why that matters. */
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    void Promise.all([loadPendingChange(), loadPasskeyCount()]).then(([state, count]) => {
      if (!live) return;
      setPending(state);
      setPasskeyCount(count);
    });
    return () => {
      live = false;
    };
  }, [nonce]);

  return { pending, passkeyCount, reload: () => setNonce((n) => n + 1) };
}

/**
 * The recovery address, and the change waiting on it (account#73).
 *
 * ── ITS OWN HOOK RATHER THAN A THIRD CALL IN THE ONE ABOVE ───────────
 *
 * `usePendingEmailChange` bundles two requests because the section it feeds
 * needs both to render one control. This is a different section with its own
 * reload: adding it there would mean cancelling a recovery change re-fetched
 * the passkey count, and a failure in either lookup would blank both.
 *
 * `undefined` until the first answer arrives, which the section renders as
 * "checking" rather than as "you have no recovery address" — the distinction
 * that matters most on this card, since the reassuring answer is the dangerous
 * one to guess.
 */
function useRecoveryAddress(): { recovery: RecoveryState | undefined; reload: () => void } {
  const [recovery, setRecovery] = useState<RecoveryState | undefined>(undefined);
  /** Bumped to re-run the effect, so there is one fetch site and the `live`
   *  guard keeps working. Same shape as the hooks either side. */
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    void loadRecoveryAddress().then((state) => {
      if (live) setRecovery(state);
    });
    return () => {
      live = false;
    };
  }, [nonce]);

  return { recovery, reload: () => setNonce((n) => n + 1) };
}

/**
 * Which providers are linked, and whether there is a password.
 *
 * ── ASKED ONCE, HERE, RATHER THAN BY EACH SECTION ────────────────────
 *
 * Two sections need the same answer: the list itself, and the Password card,
 * which has to offer "set" rather than "change" to an account that has never
 * had one. Fetching in each would be two requests for one fact, and they can
 * arrive in either order and disagree — a Password card saying "you have no
 * password" beside a list showing one is a screen arguing with itself.
 *
 * `undefined` while outstanding and `null` when the lookup failed. The
 * sections render the two differently and must be able to tell them apart: an
 * empty list drawn for a network error is a false claim about somebody's
 * account, made from no evidence.
 */
function useSignInMethods(): {
  methods: Methods | null | undefined;
  /** What this deployment could offer, for the "Add provider" control. Null
   *  when it could not be asked. */
  available: string[] | null;
  reload: () => void;
} {
  const [methods, setMethods] = useState<Methods | null | undefined>(undefined);
  const [available, setAvailable] = useState<string[] | null>(null);
  /**
   * Bumped to re-run the effect, rather than calling the loader from a second
   * place.
   *
   * One fetch site means the `live` guard keeps working: a removal followed
   * quickly by another leaves two requests in flight, and the effect's cleanup
   * is what stops the slower one landing last and restoring a row that was
   * just deleted. A separate imperative `load()` would need that guard
   * duplicated, and the copy that got it wrong would fail only under a race.
   */
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    /**
     * Both in one effect, so the `live` guard covers both and a removal
     * followed quickly by another cannot land the slower pair out of order.
     * The available list does not change between reloads in practice; it is
     * re-read anyway rather than fetched once, because a second effect with a
     * second guard is the copy that gets the race wrong.
     */
    void Promise.all([loadSignInMethods(), loadAvailableProviders()]).then(([m, a]) => {
      if (!live) return;
      setMethods(m);
      setAvailable(a);
    });
    return () => {
      live = false;
    };
  }, [nonce]);

  return { methods, available, reload: () => setNonce((n) => n + 1) };
}

/**
 * `scroll-margin-top` on the section, not padding.
 *
 * An anchor jump puts the target flush against the top of the viewport, which
 * hides the heading under anything fixed and — more mundanely — looks like the
 * page jumped to the wrong place. The margin only applies to scroll positioning,
 * so it costs nothing in layout.
 */
function Card({
  id,
  title,
  tone = 'normal',
  children,
}: {
  id: string;
  title: string;
  /**
   * `danger` colours the border and the heading. Not the background: a filled
   * red panel makes the whole card read as an error that has already happened,
   * and this one is describing something that has not.
   *
   * Colour is never the only signal — the heading says "Danger" and the button
   * inside says what it does, so a reader who cannot distinguish the border
   * loses nothing.
   */
  tone?: 'normal' | 'danger';
  children: React.ReactNode;
}) {
  const danger = tone === 'danger';
  return (
    <section
      id={id}
      /**
       * Focusable programmatically, never by tabbing. `useHashTarget` puts the
       * focus here on arrival so a keyboard or screen-reader user lands where a
       * sighted one is looking; `-1` keeps it out of the tab order, so nobody
       * tabbing through the page collects eight stops that do nothing.
       */
      tabIndex={-1}
      style={{
        // No focus ring: the focus here is a scroll destination rather than an
        // interaction, and a box outline round a whole card reads as an error.
        outline: 'none',
        scrollMarginTop: '1.5rem',
        marginBottom: '1.5rem',
        padding: '1.5rem',
        border: `1px solid ${danger ? DANGER_BORDER : C.inputBorder}`,
        borderRadius: 8,
        background: C.surface,
      }}
    >
      <h2 style={{ fontSize: '1.15rem', margin: '0 0 1rem', color: danger ? DANGER : undefined }}>
        {title}
      </h2>
      {/*
        The measure lives here, on the CONTENT, not on the card. A card narrower
        than its column leaves a band of white beside it that reads as a bug; a
        full-width card with 60ch of prose inside reads as a layout.
      */}
      <div style={{ maxWidth: '60ch' }}>{children}</div>
    </section>
  );
}

/**
 * The spacer height, remeasured when the window resizes.
 *
 * ── ON MOUNT AND RESIZE ONLY, NEVER ON SCROLL ────────────────────────
 *
 * Scrolling does not change how tall the last card is, and setting state from
 * the scroll handler would re-render the whole screen on every frame of every
 * scroll — for a value that did not move. `useActiveSection` runs on scroll
 * because the answer genuinely changes; this does not.
 *
 * A layout effect rather than an ordinary one, so the space is in place before
 * the browser paints. With `useEffect` the first frame renders a page that is
 * one spacer too short, and an anchor jump landing in that frame scrolls to a
 * position the next frame invalidates.
 *
 * Zero until measured, which is what it was before this existed.
 */
function useTrailingSpace(lastSectionId: string): number {
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const measure = () => {
      const last = document.getElementById(lastSectionId);
      if (!last) return;
      const next = trailingSpace(window.innerHeight, last.getBoundingClientRect().height);
      // Only on a real change: a resize listener that always sets state makes
      // every resize event a render even when the value is identical.
      setHeight((current) => (Math.abs(current - next) < 1 ? current : next));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [lastSectionId]);

  return height;
}

/**
 * Which section is under the top of the viewport.
 *
 * `rootMargin` pulls the observation band up to a strip near the top, so the
 * marked entry is the one you are reading rather than whichever card happens to
 * be largest on screen. Without it the last section never activates, because a
 * short final card cannot occupy the middle of a tall viewport.
 */
/**
 * Land on the section the URL names, and put the focus in it.
 *
 * ── THE BROWSER'S OWN FRAGMENT JUMP CANNOT WORK HERE ─────────────────
 *
 * It fires as soon as the document is parsed, and at that moment this app has
 * rendered nothing: `App` is still showing "Loading…" while the session request
 * is outstanding, so `#recovery` names an element that does not exist yet. The
 * browser gives up silently and the page sits at the top — which is exactly
 * what Jon saw on 2026-09-05 pressing No on the review banner and arriving at
 * an unmoved Security screen.
 *
 * Nothing was wrong with the link. The element simply arrived late, so the jump
 * has to be re-run once it exists.
 *
 * ── FOCUS, NOT ONLY SCROLL ───────────────────────────────────────────
 *
 * Scrolling moves the page for somebody who can see it and does nothing for a
 * screen reader or a keyboard user, who are left at the top of the document
 * with the next Tab going to the brand link. `Card` carries `tabIndex={-1}` so
 * it can take focus programmatically without becoming a tab stop, and
 * `preventScroll` keeps the focus call from fighting the smooth scroll below.
 *
 * ── ONCE, ON MOUNT ───────────────────────────────────────────────────
 *
 * Not on every hash change: the "On this page" rail uses ordinary anchors, and
 * those already work — the sections exist by then, and re-running this would
 * steal focus from somebody who is merely reading.
 */
export function useHashTarget(): void {
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    const target = document.getElementById(id);
    if (!target) return;
    target.focus({ preventScroll: true });
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });

    /*
     * ── AND AGAIN WHILE THE CARDS ABOVE IT FILL IN ──────────────────────
     *
     * The target exists on mount, but the cards above it do not have their
     * final height yet: two-factor, passkeys and devices each load after
     * the page renders and grow when they do. A link to `#danger` (Keyodo's
     * "Close my Ridgewill account") scrolled to where Danger WAS and came to
     * rest around Password (Jon, 2026-10-02). So while the page is still
     * changing size, the jump is repeated, for a few seconds at most, and
     * never after the person has scrolled, tapped or pressed a key: from
     * then on the page is theirs.
     */
    if (typeof ResizeObserver === 'undefined') return;
    let settled = false;
    const stop = () => {
      settled = true;
      observer.disconnect();
      window.clearTimeout(timer);
      for (const e of INTERACTIONS) window.removeEventListener(e, stop);
    };
    const observer = new ResizeObserver(() => {
      if (!settled) target.scrollIntoView({ block: 'start' });
    });
    observer.observe(document.body);
    const timer = window.setTimeout(stop, SETTLE_MS);
    for (const e of INTERACTIONS) window.addEventListener(e, stop, { passive: true });
    return stop;
  }, []);
}

/** How long a hash link keeps re-aiming while the page fills in. */
const SETTLE_MS = 3000;
/** Anything that means the person has taken the page over. */
const INTERACTIONS = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;

function useActiveSection(): string {
  const [active, setActive] = useState<string>(SECTIONS[0].id);

  useEffect(() => {
    const nodes = SECTIONS.map((s) => document.getElementById(s.id)).filter(
      (n): n is HTMLElement => n !== null,
    );
    if (nodes.length === 0) return;

    /**
     * The observer only reports what CHANGED, so the rule cannot be "the entry
     * in this callback" — it has to be applied to every section's current
     * position. `pickActive` does that and is a pure function, which is the only
     * part of this that can be tested: `IntersectionObserver` delivers callbacks
     * through the rendering lifecycle, so a headless or non-compositing page
     * fires none at all and proves nothing either way.
     */
    const recompute = () => {
      const positions = nodes.map((n) => ({ id: n.id, top: n.getBoundingClientRect().top }));
      setActive(
        pickActive(
          positions,
          window.innerHeight,
          isAtBottom(window.scrollY, window.innerHeight, document.documentElement.scrollHeight),
        ),
      );
    };

    const observer = new IntersectionObserver(recompute, {
      rootMargin: '0px 0px -70% 0px',
      threshold: 0,
    });
    nodes.forEach((n) => observer.observe(n));

    /**
     * A SCROLL LISTENER AS WELL, WHICH THIS FILE USED TO ARGUE AGAINST.
     *
     * The old argument was that the observer fires only on a boundary crossing
     * while a listener runs on every frame, and it is right about the cost. It
     * is wrong that the observer is SUFFICIENT, and the reason is structural
     * rather than a matter of tuning: arriving at the bottom of the page
     * crosses no boundary, so there is no event for the observer to deliver.
     * It cannot report the one state `pickActive`'s bottom rule needs.
     *
     * `passive`, so it never delays a scroll, and the work per call is six
     * `getBoundingClientRect` reads against a layout the browser has just done
     * for the scroll anyway.
     */
    window.addEventListener('scroll', recompute, { passive: true });
    window.addEventListener('resize', recompute);
    recompute();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', recompute);
      window.removeEventListener('resize', recompute);
    };
  }, []);

  return active;
}

/**
 * How much empty space the page needs below its last section.
 *
 * ── THE BUG THIS FIXES: A SECTION THE RAIL COULD NEVER MARK ──────────
 *
 * Jon, 2026-09-01: scrolling to the bottom went straight from `Passkeys` to
 * `Danger`, skipping `Devices` entirely, and clicking `Devices` in the rail
 * scrolled correctly while marking `Danger`.
 *
 * The cause is arithmetic, not tuning. A section becomes current when its top
 * climbs above the reading line at 30% of the viewport, and scrolling stops
 * when the document bottom meets the viewport bottom. So every section packed
 * into the last 70% of a screenful is unreachable: its top never gets high
 * enough, however far you scroll. `pickActive`'s `atBottom` rule papered over
 * that for the LAST section by asserting it, which is why `Danger` worked and
 * `Devices` — sitting between the line and the end — did not.
 *
 * Adding scroll room is the only fix that reaches every section rather than
 * trading one for another: with 70% of a viewport below the last card, the
 * last card's top can reach the line, and so can everything above it.
 *
 * Measured rather than a flat `70vh`, so a tall final card adds no space at
 * all and nobody gets a screen of white for nothing.
 */
export function trailingSpace(
  viewportHeight: number,
  lastSectionHeight: number,
  line = READING_LINE,
): number {
  return Math.max(0, viewportHeight * (1 - line) - lastSectionHeight);
}

/** Where the rail considers you to be reading, as a fraction of the viewport.
 *  Shared by `pickActive` and `trailingSpace` — they have to agree, and two
 *  copies of 0.3 is exactly the pair that drifts. */
export const READING_LINE = 0.3;

/**
 * The last section whose top has passed the reading line, or the first if none
 * has.
 *
 * ── WHY "LAST ABOVE THE LINE" AND NOT "FIRST INTERSECTING" ───────────
 *
 * A short final card cannot reach the middle of a tall viewport, so a rule based
 * on occupancy never marks it and the rail goes dead at the bottom of the page.
 * Reading position is about which heading you have most recently passed, which
 * is what this measures.
 *
 * `threshold` is a fraction of the viewport rather than a pixel count so it holds
 * on a phone and on a large display.
 */
export function pickActive(
  sections: { id: string; top: number }[],
  viewportHeight: number = typeof window === 'undefined' ? 0 : window.innerHeight,
  atBottom = false,
): string {
  /**
   * ── THE END OF THE PAGE IS A SPECIAL CASE, AND HAS TO BE ───────────
   *
   * The reading line sits at 30% of the viewport, and the last section only
   * becomes current once its top climbs above that. Whether it CAN is decided
   * by how much page is left underneath it: scrolling stops when the document
   * bottom meets the viewport bottom, so a final card shorter than 70% of the
   * viewport never gets high enough, however far you scroll.
   *
   * That is the bug Jon reported — six sections, the rail dead from `Passkeys`
   * onward, and `Devices` and `Danger` unreachable no matter what. It is not a
   * threshold that wants tuning: no value works for every combination of card
   * height and window height, because the real constraint is that the page has
   * run out.
   *
   * At the bottom, the last section is the one being read. That is true by
   * construction rather than by measurement, so it is asserted rather than
   * computed.
   */
  if (atBottom) return sections[sections.length - 1]!.id;

  const line = viewportHeight * READING_LINE;
  const passed = sections.filter((s) => s.top <= line);
  return (passed.length > 0 ? passed[passed.length - 1]! : sections[0]!).id;
}

/** Within a pixel or two of the end of the document. The slack is for
 *  fractional device pixels and zoom, where the sum lands just short and a
 *  strict equality never fires. */
export function isAtBottom(scrollY: number, viewportHeight: number, documentHeight: number): boolean {
  return scrollY + viewportHeight >= documentHeight - 2;
}

/** Shared with sections/CloseAccount, and the same red `Problem` uses.
 *
 *  TOKENS, NOT LITERALS (account#87). Every `C.*` token is
 *  `var(--rw-x, <light>)`, so a literal does not participate in the theme at
 *  all: on a dark ground this stayed a dark red on near-black. */
const DANGER = C.dangerText;
const DANGER_BORDER = C.dangerBorder;

function Contents({ active }: { active: string }) {
  return (
    /**
     * OUTSIDE THE FRAME, not inside it (Jon, 2026-08-31).
     *
     * The rail used to be a flex item sitting beside the cards INSIDE the
     * page's outer card, which cost that column a third of its width — the
     * cards were squeezed into about 430px while the rail took 190 of the 656
     * available. Moving the rail into the margin gives every one of them the
     * full frame back.
     *
     * It stays a child of the frame in the DOM, because the rail is about this
     * page and belongs with it for reading order and for the `nav` landmark.
     * Only the painting moves: absolute against the page's own wrapper, then
     * pushed out past the frame's padding and its border.
     *
     * `height: 100%` matters and is easy to drop. The absolute box is what the
     * sticky child sticks WITHIN, so a box that does not span the sections has
     * a rail that stops following partway down.
     */
    <nav
      aria-label="On this page"
      className="contents-rail"
      style={{
        position: 'absolute',
        // The page has no card of its own since the ridgewill.com frame, so
        // the gap is the whole offset.
        right: `calc(100% + ${RAIL_GAP}px)`,
        top: 0,
        height: '100%',
        width: RAIL_WIDTH,
      }}
    >
      <div style={{ position: 'sticky', top: `calc(${STICKY_TOP} + 1.5rem)` }}>
        <p
          style={{
            margin: '0 0 .5rem',
            fontSize: '.75rem',
            letterSpacing: '.06em',
            textTransform: 'uppercase',
            color: C.muted,
            /** Right-aligned with the links below it, so the whole rail reads
             *  as one column ending on the rule rather than a heading floating
             *  above a list. */
            textAlign: 'right',
            paddingRight: '.75rem',
          }}
        >
          On this page
        </p>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {SECTIONS.map((s) => {
            const on = s.id === active;
            return (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  aria-current={on ? 'true' : undefined}
                  style={{
                    display: 'block',
                    /**
                     * Text to the RIGHT, rule on the right edge, so the labels
                     * butt against the line nearest the content they point at.
                     * The marker was a LEFT border while the rail sat left of
                     * the cards; flipping the alignment without flipping the
                     * border would leave the current-section mark on the far
                     * side of the words from the thing it marks.
                     */
                    textAlign: 'right',
                    padding: '.35rem .75rem .35rem 0',
                    // Border AND weight, never colour alone.
                    borderRight: `2px solid ${on ? C.brand : C.inputBorder}`,
                    color: on ? C.text : C.textMid,
                    fontWeight: on ? 600 : 400,
                    textDecoration: 'none',
                  }}
                >
                  {s.label}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
